import { Checkbox, RadioGroup } from "@synthesis/shared-ui";
import { cn } from "@synthesis/shared-utils";
import { FormProvider, useForm } from "react-hook-form";

import { getSelectOptionLabel } from "../../../../constants";
import {
  getDictionaryOptionLabel,
  getRailwayShortName,
  type CreateApplicationDictionaries,
} from "../../../create-application-page/create-application-dictionaries.lib";
import { useApplicationDictionariesContext } from "../../application-dictionaries-context";
import {
  ADJACENT_CARGO_SENDER_OPTIONS,
  ADJACENT_LAND_DATA_AVAILABILITY_OPTIONS,
  isAdjacentLandSectionRequired,
  type AdjacentLandDataAvailability,
  type AdjacentObjectDataFormValues,
} from "../../../create-application-page/create-application-page.lib";
import type { ApplicationDetailField } from "../../application-detail-page.lib";
import { DetailSection } from "../detail-section";

type ObjectDescriptionContentProps = {
  adjacentObjectData: AdjacentObjectDataFormValues | null;
  className?: string;
};

type ObjectDescriptionDetailSection = {
  title: string;
  fields: ApplicationDetailField[];
};

const formatObjectDescriptionValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (Array.isArray(value)) {
    const items = value.filter((item): item is string => typeof item === "string" && Boolean(item.trim()));

    return items.length > 0 ? items.join(", ") : "—";
  }

  if (typeof value === "boolean") {
    return value ? "Да" : "Нет";
  }

  return String(value);
};

/**
 * Поля для cadastralNumbers в созданной заявке: каждый номер отображается в отдельной строке.
 */
const buildCadastralNumberFields = (values: string[] | undefined): ApplicationDetailField[] => {
  const numbers = (values ?? []).map((item) => item.trim()).filter(Boolean);

  if (numbers.length === 0) {
    return [{ label: "Кадастровый номер / квартал", value: "—" }];
  }

  return numbers.map((number) => ({ label: "Кадастровый номер / квартал", value: number }));
};

type CadastralDataAvailabilityFormValues = {
  cadastralDataAvailability: AdjacentLandDataAvailability;
};

/**
 * Read-only блок «У вас есть кадастровые данные об участке?» —
 * подпись сверху, обе радиокнопки снизу в ряд; активен сохранённый ответ.
 */
const CadastralDataAvailabilityField = ({ value }: { value: AdjacentLandDataAvailability | undefined }) => {
  const methods = useForm<CadastralDataAvailabilityFormValues>({
    defaultValues: { cadastralDataAvailability: value ?? "unknown" },
    values: { cadastralDataAvailability: value ?? "unknown" },
  });

  return (
    <div className="border-neutrals-90 flex flex-col gap-2 border-b py-3">
      <p className="paragraph-base text-type-minor">У вас есть кадастровые данные об участке?</p>
      <FormProvider {...methods}>
        <RadioGroup
          name="cadastralDataAvailability"
          horizontal
          disabled
          renderRadioGroupElements={ADJACENT_LAND_DATA_AVAILABILITY_OPTIONS.map((option) => ({
            id: `detail-cadastral-data-${option.value}`,
            value: option.value,
            label: option.label,
          }))}
        />
      </FormProvider>
    </div>
  );
};

const buildAdjacentObjectDescriptionSections = (
  adjacentObjectData: AdjacentObjectDataFormValues | null,
  dictionaries: CreateApplicationDictionaries,
): ObjectDescriptionDetailSection[] => {
  const showLandSection = adjacentObjectData ? isAdjacentLandSectionRequired(adjacentObjectData.plannedWorkType) : false;

  const sections: ObjectDescriptionDetailSection[] = [
    {
      title: "Общая информация об объекте",
      fields: [
        { label: "Название проекта / объекта примыкания", value: formatObjectDescriptionValue(adjacentObjectData?.projectName) },
        {
          label: "Вид планируемых работ",
          value: getDictionaryOptionLabel(dictionaries.plannedWorksTypes, adjacentObjectData?.plannedWorkType),
        },
        {
          label: "Тип примыкания",
          value: getDictionaryOptionLabel(dictionaries.adjoiningTypes, adjacentObjectData?.connectionType),
        },
        { label: "Функциональное назначение пути", value: formatObjectDescriptionValue(adjacentObjectData?.trackPurpose) },
        {
          label: "Предварительный источник финансирования",
          value: getDictionaryOptionLabel(dictionaries.trackFundings, adjacentObjectData?.fundingSource),
        },
        ...(adjacentObjectData?.fundingSource?.toUpperCase() === "OTHER"
          ? [{ label: "Источник финансирования", value: formatObjectDescriptionValue(adjacentObjectData.fundingSourceOther) }]
          : []),
        { label: "Протяженность пути, м", value: formatObjectDescriptionValue(adjacentObjectData?.pathLengthMeters) },
        { label: "Краткое описание проектных решений", value: formatObjectDescriptionValue(adjacentObjectData?.projectDescription) },
        {
          label: "",
          fullWidth: true,
          value: (
            <Checkbox
              checked={Boolean(adjacentObjectData?.licensedSubsoilDevelopment)}
              disabled
              label="Проект связан с разработкой участков недр, подлежащей лицензированию"
            />
          ),
        },
        ...(adjacentObjectData?.licensedSubsoilDevelopment
          ? [
              {
                label: "Номер и дата лицензии на пользование недрами",
                value: formatObjectDescriptionValue(adjacentObjectData.subsoilLicenseNumberAndDate),
              },
              { label: "Срок действия лицензии", value: formatObjectDescriptionValue(adjacentObjectData.subsoilLicenseValidity) },
            ]
          : []),
        {
          label: "",
          fullWidth: true,
          value: (
            <Checkbox
              checked={Boolean(adjacentObjectData?.hasDesignDocumentationExpertise)}
              disabled
              label="Есть заключение экспертизы проектной документации"
            />
          ),
        },
        ...(adjacentObjectData?.hasDesignDocumentationExpertise
          ? [
              {
                label: "Номер и дата заключения экспертизы",
                value: formatObjectDescriptionValue(adjacentObjectData.designDocumentationExpertiseDetails),
              },
            ]
          : []),
      ],
    },
  ];

  if (showLandSection) {
    sections.push({
      title: "Земельный участок",
      fields: [
        {
          value: <CadastralDataAvailabilityField value={adjacentObjectData?.cadastralDataAvailability} />,
        },
        ...(adjacentObjectData?.cadastralDataAvailability === "known"
          ? buildCadastralNumberFields(adjacentObjectData.cadastralNumbers)
          : [
              {
                label: "Местоположение участка",
                value: formatObjectDescriptionValue(adjacentObjectData?.landLocationDescription),
              },
            ]),
      ],
    });
  }

  sections.push(
    {
      title: "Точка примыкания",
      fields: [
        { label: "Железная дорога (филиал РЖД)", value: getRailwayShortName(dictionaries.railways, adjacentObjectData?.railway) },
        { label: "Станция / Перегон", value: formatObjectDescriptionValue(adjacentObjectData?.stationSection) },
        { label: "Км, пикет и метр", value: formatObjectDescriptionValue(adjacentObjectData?.junctionLocation) },
        { label: "Координаты", value: formatObjectDescriptionValue(adjacentObjectData?.coordinates) },
        { label: "Ближайший существующий путь", value: formatObjectDescriptionValue(adjacentObjectData?.nearestExistingTrack) },
        { label: "Комментарий к месту примыкания", value: formatObjectDescriptionValue(adjacentObjectData?.connectionPlaceComment) },
      ],
    },
    {
      title: "Перевозки и грузопотоки",
      fields: [
        {
          label: "Заявитель является грузоотправителем",
          value: getSelectOptionLabel(ADJACENT_CARGO_SENDER_OPTIONS, adjacentObjectData?.applicantIsCargoSender),
        },
      ],
    },
    {
      title: "Сроки реализации проекта",
      fields: [
        { label: "Планируемый срок начала работ", value: formatObjectDescriptionValue(adjacentObjectData?.implementationStartDate) },
        { label: "Планируемый срок окончания работ", value: formatObjectDescriptionValue(adjacentObjectData?.implementationEndDate) },
        { label: "Планируемый срок ввода в эксплуатацию", value: formatObjectDescriptionValue(adjacentObjectData?.commissioningDate) },
      ],
    },
  );

  return sections;
};

export const ObjectDescriptionContent = ({ adjacentObjectData, className }: ObjectDescriptionContentProps) => {
  const { dictionaries } = useApplicationDictionariesContext();

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      {buildAdjacentObjectDescriptionSections(adjacentObjectData, dictionaries).map((section) => (
        <DetailSection key={section.title} title={section.title} fields={section.fields} />
      ))}

      {adjacentObjectData?.guaranteeLettersRequired ? (
        <Checkbox checked disabled label="Заявитель не является грузоотправителем — нужны гарантийные письма грузоотправителей" />
      ) : null}
    </div>
  );
};
